import { useEffect, useState, useRef } from "react";
import { API } from "../../App";
import AdminLayout from "../../components/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Plus, Edit2, Trash2, MapPin, ChefHat, Truck, Loader2, Leaf, Drumstick, Salad, Clock, Upload, Image as ImageIcon, Users, Navigation, Search, Building2, Copy } from "lucide-react";
import { toast } from "sonner";
import { GoogleMap, useJsApiLoader, Marker as GMarker, Autocomplete } from "@react-google-maps/api";
import SegmentMultiSelect from "../../components/SegmentMultiSelect";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
const GOOGLE_LIBRARIES = ["places"];
const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const DAY_LABELS = { monday: "Mon", tuesday: "Tue", wednesday: "Wed", thursday: "Thu", friday: "Fri", saturday: "Sat", sunday: "Sun" };

export default function AdminKitchens() {
  const [kitchens, setKitchens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingKitchen, setEditingKitchen] = useState(null);
  const [chefs, setChefs] = useState([]);
  const [riders, setRiders] = useState([]);
  const [managers, setManagers] = useState([]);
  const [cooks, setCooks] = useState([]);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [kitRes, chefRes, riderRes, mgrRes, cookRes] = await Promise.all([
        fetch(`${API}/kitchens`, { credentials: "include" }),
        fetch(`${API}/chefs`, { credentials: "include" }),
        fetch(`${API}/riders`, { credentials: "include" }),
        fetch(`${API}/managers`, { credentials: "include" }),
        fetch(`${API}/cooks`, { credentials: "include" }),
      ]);
      if (kitRes.ok) setKitchens(await kitRes.json());
      if (chefRes.ok) setChefs(await chefRes.json());
      if (riderRes.ok) setRiders(await riderRes.json());
      if (mgrRes.ok) setManagers(await mgrRes.json());
      if (cookRes.ok) setCooks(await cookRes.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleDelete = async (kitchenId) => {
    if (!window.confirm("Delete this kitchen?")) return;
    try {
      const res = await fetch(`${API}/admin/kitchens/${kitchenId}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) { const err = await res.json(); toast.error(err.detail); return; }
      setKitchens((prev) => prev.filter((k) => k.kitchen_id !== kitchenId));
      toast.success("Kitchen deleted");
    } catch { toast.error("Failed to delete"); }
  };

  const handleDuplicate = async (kitchenId) => {
    try {
      const res = await fetch(`${API}/admin/kitchens/${kitchenId}/duplicate`, { method: "POST", credentials: "include" });
      if (!res.ok) { const err = await res.json(); toast.error(err.detail); return; }
      const dup = await res.json();
      setKitchens((prev) => [...prev, dup]);
      toast.success(`Kitchen duplicated as ${dup.kitchen_code}`);
    } catch { toast.error("Failed to duplicate"); }
  };

  const getName = (id, list) => { const u = list.find((x) => x.user_id === id); return u ? u.name || u.email : "Unassigned"; };
  const getNames = (ids, list) => (!ids?.length) ? "None" : ids.map((id) => getName(id, list)).join(", ");

  return (
    <AdminLayout>
      <div className="animate-fade-in" data-testid="admin-kitchens">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Kitchen Locations</h1>
            <p className="text-sm text-gray-500 mt-1">{kitchens.length} kitchen{kitchens.length !== 1 ? "s" : ""}</p>
          </div>
          <Button onClick={() => { setEditingKitchen(null); setShowForm(true); }} className="bg-green-600 hover:bg-green-700 text-white" data-testid="add-kitchen-btn">
            <Plus size={16} className="mr-1" /> Add Kitchen
          </Button>
        </div>

        {loading ? (
          <div className="space-y-4">{[1, 2].map((i) => <div key={i} className="h-32 bg-gray-100 rounded-xl animate-pulse" />)}</div>
        ) : kitchens.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
            <ChefHat className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No kitchens configured yet</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {kitchens.map((kit) => (
              <div key={kit.kitchen_id} className="bg-white rounded-2xl border border-gray-100 p-5" data-testid={`kitchen-card-${kit.kitchen_id}`}>
                <div className="flex items-start gap-4">
                  {kit.image_url ? (
                    <img src={`${BACKEND_URL}${kit.image_url}`} alt={kit.kitchen_code} className="w-20 h-20 rounded-xl object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-20 h-20 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0"><ImageIcon size={24} className="text-gray-300" /></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="text-lg font-bold text-gray-900 font-mono tracking-wide">{kit.kitchen_code}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${kit.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                        {kit.is_active ? "Active" : "Inactive"}
                      </span>
                      {kit.serviceable_radius_km && <span className="text-[10px] text-gray-500">{kit.serviceable_radius_km} km radius</span>}
                    </div>
                    <div className="flex items-center gap-1 text-sm text-gray-600"><MapPin size={14} className="text-gray-400" />{kit.location || `${kit.locality}, ${kit.city}`}</div>
                    <p className="text-[10px] text-gray-500">{kit.city}{kit.locality ? ` - ${kit.locality}` : ""}</p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {kit.serves_vegan && <span className="flex items-center gap-1 text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded-full"><Leaf size={10} /> Vegan</span>}
                      {kit.serves_vegetarian && <span className="flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full"><Salad size={10} /> Veg</span>}
                      {kit.serves_non_vegetarian && <span className="flex items-center gap-1 text-xs bg-red-50 text-red-700 px-2 py-0.5 rounded-full"><Drumstick size={10} /> Non-Veg</span>}
                    </div>
                    <div className="flex flex-wrap gap-4 mt-2 text-xs">
                      <span><ChefHat size={12} className="inline text-orange-500" /> Chef: <b>{getName(kit.assigned_chef_id, chefs)}</b></span>
                      <span><Truck size={12} className="inline text-blue-500" /> Riders: <b>{getNames(kit.assigned_rider_ids, riders)}</b></span>
                      <span><Users size={12} className="inline text-indigo-500" /> Manager: <b>{kit.assigned_manager_ids?.length ? getName(kit.assigned_manager_ids[0], managers) : "Unassigned"}</b></span>
                    </div>
                    {kit.gst_number && (
                      <div className="flex flex-wrap gap-3 mt-1.5 text-[9px] text-gray-500">
                        {kit.business_name && <span>Business: {kit.business_name}</span>}
                        <span>GST: {kit.gst_number}</span>
                        {kit.pan && <span>PAN: {kit.pan}</span>}
                      </div>
                    )}
                    {kit.operating_hours && Object.keys(kit.operating_hours).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {DAYS.map((d) => {
                          const h = kit.operating_hours[d];
                          return h?.open ? (
                            <span key={d} className="text-[10px] bg-gray-50 px-1.5 py-0.5 rounded text-gray-600">{DAY_LABELS[d]} {h.open}-{h.close}</span>
                          ) : null;
                        })}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={() => { setEditingKitchen(kit); setShowForm(true); }} className="p-2 text-gray-400 hover:text-green-600" data-testid={`edit-kitchen-${kit.kitchen_id}`}><Edit2 size={16} /></button>
                    <button onClick={() => handleDuplicate(kit.kitchen_id)} className="p-2 text-gray-400 hover:text-blue-500" data-testid={`duplicate-kitchen-${kit.kitchen_id}`} title="Duplicate"><Copy size={16} /></button>
                    <button onClick={() => handleDelete(kit.kitchen_id)} className="p-2 text-gray-400 hover:text-red-500" data-testid={`delete-kitchen-${kit.kitchen_id}`}><Trash2 size={16} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <Dialog open={showForm} onOpenChange={(o) => { if (!o) { setShowForm(false); setEditingKitchen(null); } }}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editingKitchen ? "Edit Kitchen" : "Add Kitchen"}</DialogTitle></DialogHeader>
            <KitchenForm kitchen={editingKitchen} chefs={chefs} riders={riders} managers={managers} cooks={cooks} kitchens={kitchens}
              onSave={() => { setShowForm(false); setEditingKitchen(null); fetchAll(); }}
              onCancel={() => { setShowForm(false); setEditingKitchen(null); }} />
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}

function KitchenForm({ kitchen, chefs, riders, managers, cooks, kitchens, onSave, onCancel }) {
  const fileRef = useRef(null);
  const autocompleteRef = useRef(null);
  const mapRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const [form, setForm] = useState({
    kitchen_code: kitchen?.kitchen_code || "",
    location: kitchen?.location || "",
    lat: kitchen?.lat || 20.5937, lng: kitchen?.lng || 78.9629,
    city: kitchen?.city || "",
    locality: kitchen?.locality || "",
    serves_vegan: kitchen?.serves_vegan || false,
    serves_vegetarian: kitchen?.serves_vegetarian || false,
    serves_non_vegetarian: kitchen?.serves_non_vegetarian || false,
    is_active: kitchen?.is_active ?? true,
    assigned_chef_id: kitchen?.assigned_chef_id || "",
    assigned_rider_ids: kitchen?.assigned_rider_ids || [],
    assigned_manager_ids: kitchen?.assigned_manager_ids?.length ? [kitchen.assigned_manager_ids[0]] : kitchen?.assigned_manager_id ? [kitchen.assigned_manager_id] : [],
    assigned_cook_ids: kitchen?.assigned_cook_ids || [],
    serviceable_radius_km: kitchen?.serviceable_radius_km || 10,
    operating_hours: kitchen?.operating_hours || {},
    image_url: kitchen?.image_url || "",
    business_name: kitchen?.business_name || "",
    business_phone: kitchen?.business_phone || "",
    business_email: kitchen?.business_email || "",
    business_address: kitchen?.business_address || "",
    cin: kitchen?.cin || "",
    pan: kitchen?.pan || "",
    gst_number: kitchen?.gst_number || "",
    google_maps_link: kitchen?.google_maps_link || "",
    allowed_segment_ids: kitchen?.allowed_segment_ids || [],
  });
  const [saving, setSaving] = useState(false);

  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: GOOGLE_MAPS_KEY, libraries: GOOGLE_LIBRARIES });
  const indiaBounds = { north: 35.5, south: 6.5, east: 97.5, west: 68.0 };

  const assignedChefIds = kitchens.filter((k) => k.kitchen_id !== kitchen?.kitchen_id).map((k) => k.assigned_chef_id).filter(Boolean);
  const assignedRiderIds = kitchens.filter((k) => k.kitchen_id !== kitchen?.kitchen_id).flatMap((k) => k.assigned_rider_ids || []);

  const reverseGeocode = async (lat, lng) => {
    if (!window.google?.maps) return;
    try {
      const geocoder = new window.google.maps.Geocoder();
      const response = await geocoder.geocode({ location: { lat, lng } });
      const result = response.results?.[0];
      if (!result) return;
      const components = result.address_components || [];
      let city = "", locality = "";
      for (const c of components) {
        if (c.types.includes("locality")) city = c.long_name;
        if (c.types.includes("sublocality_level_1")) locality = c.long_name;
        if (!city && c.types.includes("administrative_area_level_2")) city = c.long_name;
      }
      setForm((prev) => ({
        ...prev, lat, lng,
        location: result.formatted_address || prev.location,
        city: city || prev.city,
        locality: locality || prev.locality,
      }));
    } catch (e) { console.error("Reverse geocode failed:", e); }
  };

  const onPlaceSelected = () => {
    const place = autocompleteRef.current?.getPlace();
    if (!place?.geometry) return;
    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    const components = place.address_components || [];
    let city = "", locality = "";
    for (const c of components) {
      if (c.types.includes("locality")) city = c.long_name;
      if (c.types.includes("sublocality_level_1")) locality = c.long_name;
      if (!city && c.types.includes("administrative_area_level_2")) city = c.long_name;
    }
    setForm((prev) => ({
      ...prev, lat, lng,
      location: place.formatted_address || place.name || prev.location,
      city: city || prev.city,
      locality: locality || prev.locality,
    }));
    mapRef.current?.panTo({ lat, lng });
    mapRef.current?.setZoom(16);
  };

  const handleMapClick = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setForm((prev) => ({ ...prev, lat, lng }));
    reverseGeocode(lat, lng);
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setForm((prev) => ({ ...prev, lat: latitude, lng: longitude }));
        reverseGeocode(latitude, longitude);
        mapRef.current?.panTo({ lat: latitude, lng: longitude });
        mapRef.current?.setZoom(16);
        setGeoLoading(false);
        toast.success("Location captured!");
      },
      () => { setGeoLoading(false); toast.error("Could not get location"); },
      { timeout: 8000, enableHighAccuracy: true }
    );
  };

  const setHour = (day, field, val) => {
    setForm((p) => ({ ...p, operating_hours: { ...p.operating_hours, [day]: { ...p.operating_hours[day], [field]: val } } }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !kitchen?.kitchen_id) { toast.info("Save kitchen first, then upload image"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await fetch(`${API}/admin/kitchens/${kitchen.kitchen_id}/image`, { method: "POST", credentials: "include", body: fd });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setForm((p) => ({ ...p, image_url: data.image_url }));
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const toggleRider = (id) => setForm((p) => ({ ...p, assigned_rider_ids: p.assigned_rider_ids.includes(id) ? p.assigned_rider_ids.filter((x) => x !== id) : [...p.assigned_rider_ids, id] }));

  const handleSubmit = async () => {
    if (!form.kitchen_code.trim()) { toast.error("Kitchen code required"); return; }
    if (!form.city.trim()) { toast.error("City required"); return; }
    setSaving(true);
    try {
      const url = kitchen ? `${API}/admin/kitchens/${kitchen.kitchen_id}` : `${API}/admin/kitchens`;
      const res = await fetch(url, { method: kitchen ? "PUT" : "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify(form) });
      if (!res.ok) { const err = await res.json(); toast.error(err.detail); setSaving(false); return; }
      toast.success(kitchen ? "Updated" : "Created");
      onSave();
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="kitchen-form">
      {/* Image Upload */}
      <div className="flex items-center gap-4">
        {form.image_url ? (
          <img src={`${BACKEND_URL}${form.image_url}`} alt="Kitchen" className="w-20 h-20 rounded-xl object-cover" />
        ) : (
          <div className="w-20 h-20 rounded-xl bg-gray-100 flex items-center justify-center"><ImageIcon size={24} className="text-gray-300" /></div>
        )}
        <div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
          <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading || !kitchen?.kitchen_id} data-testid="kitchen-image-upload">
            {uploading ? <Loader2 size={14} className="animate-spin mr-1" /> : <Upload size={14} className="mr-1" />}
            {kitchen?.kitchen_id ? "Upload Image" : "Save first to upload"}
          </Button>
          <p className="text-[9px] text-gray-500 mt-1">Recommended: 400x300px</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs text-gray-500 mb-1 block">Kitchen Code *</Label><Input value={form.kitchen_code} onChange={(e) => setForm({ ...form, kitchen_code: e.target.value.toUpperCase() })} placeholder="KIT-MUM-01" data-testid="kitchen-code-input" /></div>
        <div><Label className="text-xs text-gray-500 mb-1 block">Radius (km)</Label><Input type="number" min="1" value={form.serviceable_radius_km} onChange={(e) => setForm({ ...form, serviceable_radius_km: parseInt(e.target.value) || 1 })} data-testid="kitchen-radius-input" /></div>
      </div>

      {/* Google Places Search */}
      <div>
        <Label className="text-xs text-gray-500 mb-1 block">Search Location</Label>
        {isLoaded && (
          <Autocomplete
            onLoad={(auto) => (autocompleteRef.current = auto)}
            onPlaceChanged={onPlaceSelected}
            options={{ componentRestrictions: { country: "in" }, types: ["geocode", "establishment"] }}
          >
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input placeholder="Search for a location in India..." className="pl-9 h-10" data-testid="kitchen-location-search" />
            </div>
          </Autocomplete>
        )}
      </div>

      {/* Google Map */}
      {isLoaded ? (
        <div className="h-44 rounded-xl overflow-hidden border border-gray-200">
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "100%" }}
            center={{ lat: form.lat, lng: form.lng }}
            zoom={kitchen?.lat ? 14 : 5}
            onClick={handleMapClick}
            onLoad={(map) => { mapRef.current = map; }}
            options={{ restriction: { latLngBounds: indiaBounds, strictBounds: true }, mapTypeControl: false, streetViewControl: false, fullscreenControl: false }}
          >
            <GMarker position={{ lat: form.lat, lng: form.lng }} />
          </GoogleMap>
        </div>
      ) : (
        <div className="h-44 rounded-xl bg-gray-100 flex items-center justify-center"><Loader2 size={24} className="animate-spin text-gray-400" /></div>
      )}
      <div className="flex items-center justify-between -mt-2">
        <p className="text-[9px] text-gray-500">Tap on the map to select location</p>
        <button type="button" onClick={useCurrentLocation} className="flex items-center gap-1 text-xs text-blue-600 font-medium hover:text-blue-700" data-testid="kitchen-use-current-location">
          <Navigation size={12} /> {geoLoading ? "Detecting..." : "Use Current Location"}
        </button>
      </div>

      {/* Auto-filled location fields */}
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs text-gray-500 mb-1 block">City *</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Mumbai" data-testid="kitchen-city-input" /></div>
        <div><Label className="text-xs text-gray-500 mb-1 block">Locality</Label><Input value={form.locality} onChange={(e) => setForm({ ...form, locality: e.target.value })} placeholder="Andheri West" data-testid="kitchen-locality-input" /></div>
      </div>
      <div><Label className="text-xs text-gray-500 mb-1 block">Full Address</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Auto-filled from map" data-testid="kitchen-location-input" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs text-gray-500 mb-1 block">Latitude</Label><Input type="number" step="any" value={form.lat || ""} onChange={(e) => setForm({ ...form, lat: e.target.value ? parseFloat(e.target.value) : null })} data-testid="kitchen-lat-input" readOnly className="bg-gray-50" /></div>
        <div><Label className="text-xs text-gray-500 mb-1 block">Longitude</Label><Input type="number" step="any" value={form.lng || ""} onChange={(e) => setForm({ ...form, lng: e.target.value ? parseFloat(e.target.value) : null })} data-testid="kitchen-lng-input" readOnly className="bg-gray-50" /></div>
      </div>
      <div><Label className="text-xs text-gray-500 mb-1 block">Google Maps Link</Label><Input value={form.google_maps_link} onChange={(e) => setForm({ ...form, google_maps_link: e.target.value })} placeholder="https://maps.app.goo.gl/..." data-testid="kitchen-google-maps-link" /></div>

      {/* Operating Hours */}
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1"><Clock size={12} /> Operating Hours <span className="text-gray-400 font-normal ml-1">(24-hour format)</span></p>
        <div className="space-y-1.5">
          {DAYS.map((day) => (
            <div key={day} className="flex items-center gap-2">
              <span className="w-10 text-xs font-medium text-gray-600">{DAY_LABELS[day]}</span>
              <input type="time" value={form.operating_hours[day]?.open || ""} onChange={(e) => setHour(day, "open", e.target.value)}
                className="border border-gray-200 rounded px-2 py-1 text-xs w-28 font-mono" data-testid={`hours-${day}-open`} step="60" />
              <span className="text-[10px] text-gray-500">to</span>
              <input type="time" value={form.operating_hours[day]?.close || ""} onChange={(e) => setHour(day, "close", e.target.value)}
                className="border border-gray-200 rounded px-2 py-1 text-xs w-28 font-mono" data-testid={`hours-${day}-close`} step="60" />
              {form.operating_hours[day]?.open && form.operating_hours[day]?.close && (
                <span className="text-[9px] text-gray-500 font-mono">{form.operating_hours[day].open} - {form.operating_hours[day].close}</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Legal & Business Details */}
      <div className="space-y-3">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1"><Building2 size={12} /> Legal & Business Details</p>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs text-gray-500 mb-1 block">Business Name</Label><Input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} placeholder="Super Morsel Pvt. Ltd." data-testid="kitchen-business-name" /></div>
          <div><Label className="text-xs text-gray-500 mb-1 block">Business Phone</Label><Input value={form.business_phone} onChange={(e) => setForm({ ...form, business_phone: e.target.value })} placeholder="+919621886633" data-testid="kitchen-business-phone" /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs text-gray-500 mb-1 block">Business Email</Label><Input value={form.business_email} onChange={(e) => setForm({ ...form, business_email: e.target.value })} placeholder="billing@supermorsel.com" data-testid="kitchen-business-email" /></div>
          <div><Label className="text-xs text-gray-500 mb-1 block">GST Number</Label><Input value={form.gst_number} onChange={(e) => setForm({ ...form, gst_number: e.target.value.toUpperCase() })} placeholder="27AABCS1234F1Z5" data-testid="kitchen-gst-number" /></div>
        </div>
        <div><Label className="text-xs text-gray-500 mb-1 block">Business Address (for invoices)</Label><Input value={form.business_address} onChange={(e) => setForm({ ...form, business_address: e.target.value })} placeholder="Registered office address" data-testid="kitchen-business-address" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs text-gray-500 mb-1 block">CIN</Label><Input value={form.cin} onChange={(e) => setForm({ ...form, cin: e.target.value.toUpperCase() })} placeholder="U56101MH2024PTC123456" data-testid="kitchen-cin" /></div>
          <div><Label className="text-xs text-gray-500 mb-1 block">PAN</Label><Input value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })} placeholder="AABCS1234F" data-testid="kitchen-pan" /></div>
        </div>
        <p className="text-[9px] text-gray-500">These details will appear on customer invoices generated for this kitchen.</p>
      </div>

      {/* Diet Toggles */}
      <div className="space-y-2">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Dietary</p>
        {[["serves_vegan", "Vegan", Leaf, "text-green-600"], ["serves_vegetarian", "Vegetarian", Salad, "text-emerald-600"], ["serves_non_vegetarian", "Non-Vegetarian", Drumstick, "text-red-600"]].map(([key, lbl, Icon, clr]) => (
          <div key={key} className="flex items-center justify-between">
            <div className="flex items-center gap-2"><Icon size={14} className={clr} /><span className="text-sm">{lbl}</span></div>
            <Switch checked={form[key]} onCheckedChange={(v) => setForm({ ...form, [key]: v })} data-testid={`kitchen-${key}-toggle`} />
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between"><span className="text-sm font-medium">Kitchen Active</span><Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="kitchen-active-toggle" /></div>

      {/* Customer Segments */}
      <SegmentMultiSelect
        value={form.allowed_segment_ids}
        onChange={(ids) => setForm({ ...form, allowed_segment_ids: ids })}
        label="Visible to Customer Segments"
        helperText="Leave empty to show this kitchen & its menu to all customers."
        testIdPrefix="kitchen-form-segments"
      />

      {/* Chef */}
      <div>
        <Label className="text-xs text-gray-500 mb-1 block">Assigned Chef</Label>
        {chefs.length === 0 ? <p className="text-xs text-orange-500">No chefs. Promote a user first.</p> : (
          <Select value={form.assigned_chef_id || "none"} onValueChange={(v) => setForm({ ...form, assigned_chef_id: v === "none" ? "" : v })}>
            <SelectTrigger data-testid="kitchen-chef-select"><SelectValue placeholder="Select chef" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {chefs.map((c) => <SelectItem key={c.user_id} value={c.user_id} disabled={assignedChefIds.includes(c.user_id)}>{c.name || c.email}{assignedChefIds.includes(c.user_id) ? " (Taken)" : ""}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Manager */}
      <div>
        <Label className="text-xs text-gray-500 mb-1 block">Assigned Manager</Label>
        {managers.length === 0 ? <p className="text-xs text-orange-500">No managers.</p> : (
          <Select value={form.assigned_manager_ids[0] || "none"} onValueChange={(v) => setForm((p) => ({ ...p, assigned_manager_ids: v === "none" ? [] : [v] }))}>
            <SelectTrigger className="bg-white" data-testid="manager-select">
              <SelectValue placeholder="Select manager" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No Manager</SelectItem>
              {managers.map((m) => (
                <SelectItem key={m.user_id} value={m.user_id}>{m.name || m.email}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <p className="text-[9px] text-gray-500 mt-1">One manager per kitchen. A manager can manage multiple kitchens.</p>
      </div>

      {/* Riders */}
      <div>
        <Label className="text-xs text-gray-500 mb-1 block">Assigned Riders (multiple)</Label>
        {riders.length === 0 ? <p className="text-xs text-orange-500">No riders.</p> : (
          <div className="space-y-1 max-h-28 overflow-y-auto border border-gray-200 rounded-lg p-2">
            {riders.map((r) => {
              const taken = assignedRiderIds.includes(r.user_id);
              const sel = form.assigned_rider_ids.includes(r.user_id);
              return (
                <label key={r.user_id} className={`flex items-center gap-2 p-1.5 rounded-lg cursor-pointer ${sel ? "bg-blue-50" : "hover:bg-gray-50"} ${taken && !sel ? "opacity-50" : ""}`}>
                  <input type="checkbox" checked={sel} onChange={() => toggleRider(r.user_id)} disabled={taken && !sel} className="rounded border-gray-300" data-testid={`rider-checkbox-${r.user_id}`} />
                  <span className="text-sm">{r.name || r.email}{taken && !sel ? " (Taken)" : ""}</span>
                </label>
              );
            })}
          </div>
        )}
      </div>

      {/* Cooks */}
      <div>
        <Label className="text-xs text-gray-500 mb-1 block">Assigned Cooks (multiple)</Label>
        {cooks.length === 0 ? <p className="text-xs text-orange-500">No cooks. Promote a user to "Cook" first.</p> : (
          <div className="space-y-1 max-h-28 overflow-y-auto border border-gray-200 rounded-lg p-2">
            {cooks.map((c) => {
              const sel = form.assigned_cook_ids.includes(c.user_id);
              return (
                <label key={c.user_id} className={`flex items-center gap-2 p-1.5 rounded-lg cursor-pointer ${sel ? "bg-emerald-50" : "hover:bg-gray-50"}`}>
                  <input
                    type="checkbox"
                    checked={sel}
                    onChange={() => setForm((p) => ({ ...p, assigned_cook_ids: sel ? p.assigned_cook_ids.filter((x) => x !== c.user_id) : [...p.assigned_cook_ids, c.user_id] }))}
                    className="rounded border-gray-300"
                    data-testid={`cook-checkbox-${c.user_id}`}
                  />
                  <span className="text-sm">{c.name || c.email}</span>
                </label>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex gap-2 pt-2">
        <Button variant="outline" onClick={onCancel} className="flex-1">Cancel</Button>
        <Button onClick={handleSubmit} disabled={saving} className="flex-1 bg-green-600 hover:bg-green-700 text-white" data-testid="save-kitchen-btn">
          {saving ? <><Loader2 size={14} className="animate-spin mr-1" /> Saving...</> : (kitchen ? "Update" : "Create")}
        </Button>
      </div>
    </div>
  );
}
